import React from 'react';
import { cn } from '@/lib/utils';
import { Clock, RotateCcw, PlaneLanding } from 'lucide-react';

interface LifeIndicatorProps {
  label: string;
  hours?: number;
  /** Secondary metric - either cycles or landings */
  secondary?: number;
  /** Label for secondary metric: 'cycles' shows cyc, 'landings' shows ldg */
  secondaryType?: 'cycles' | 'landings';
  /** @deprecated Use secondary + secondaryType instead */
  cycles?: number;
  lifeRemaining?: number;
  lifeLimit?: number;
  compact?: boolean;
  className?: string;
  /** If true, shows placeholder when no data instead of hiding */
  showPlaceholder?: boolean;
}

export const LifeIndicator: React.FC<LifeIndicatorProps> = ({
  label,
  hours,
  secondary,
  secondaryType = 'cycles',
  cycles, // deprecated, fallback
  lifeRemaining,
  lifeLimit,
  compact = false,
  className,
  showPlaceholder = true
}) => {
  // Use new secondary prop or fall back to deprecated cycles
  const secondaryValue = secondary ?? cycles;
  const isLandings = secondaryType === 'landings';
  const secondaryLabel = isLandings ? 'ldg' : 'cyc';
  const SecondaryIcon = isLandings ? PlaneLanding : RotateCcw;

  // Calculate percentage remaining
  const hasLifeData = lifeRemaining != null && lifeLimit != null && lifeLimit > 0;
  const percentRemaining = hasLifeData 
    ? Math.round((lifeRemaining / lifeLimit) * 100) 
    : null;

  const hasData = hours != null || secondaryValue != null;

  // Color coding based on percentage
  const getProgressColor = (percent: number | null) => {
    if (percent === null) return 'bg-muted-foreground/20';
    if (percent >= 70) return 'bg-gradient-to-r from-emerald-500 to-emerald-400';
    if (percent >= 40) return 'bg-gradient-to-r from-amber-500 to-amber-400';
    return 'bg-gradient-to-r from-red-500 to-red-400';
  };

  const getTextColor = (percent: number | null) => {
    if (percent === null) return 'text-muted-foreground';
    if (percent >= 70) return 'text-emerald-600 dark:text-emerald-400';
    if (percent >= 40) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Consistent neutral background for all tiles - only progress bar and badge are color-coded
  const neutralBgGradient = 'bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-900/50 dark:to-slate-800/30';

  // Compact view
  if (compact) {
    if (!hasData && !showPlaceholder) return null;
    
    return (
      <div className={cn("flex items-center gap-1.5 text-xs", className)}>
        <Clock className="w-3 h-3 text-muted-foreground" />
        <span className={cn("font-medium", !hasData && "text-muted-foreground")}>
          {hours != null ? hours.toLocaleString() : '—'}h
        </span>
        {(secondaryValue != null || showPlaceholder) && (
          <>
            <span className="text-muted-foreground">/</span>
            <SecondaryIcon className="w-3 h-3 text-muted-foreground" />
            <span className={cn("font-medium", secondaryValue == null && "text-muted-foreground")}>
              {secondaryValue != null ? secondaryValue.toLocaleString() : '—'}{secondaryLabel}
            </span>
          </>
        )}
      </div>
    );
  }

  // Standard view - fixed structure for perfect alignment across all tiles
  return (
    <div className={cn(
      "p-2 rounded-xl border shadow-sm transition-all duration-200 hover:shadow-md overflow-hidden",
      neutralBgGradient,
      "border-slate-200/60 dark:border-slate-700/40",
      className
    )}>
      {/* Header row - FIXED HEIGHT with reserved badge slot for consistent alignment */}
      <div className="flex items-center justify-between h-4 mb-1">
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold leading-none">
          {label}
        </span>
        {/* Badge slot - always reserve space, show badge or invisible placeholder */}
        <span className={cn(
          "text-[8px] font-bold px-1 py-0.5 rounded min-w-[28px] text-center leading-none",
          percentRemaining !== null ? [
            percentRemaining >= 70 && "bg-emerald-100 dark:bg-emerald-900/40",
            percentRemaining >= 40 && percentRemaining < 70 && "bg-amber-100 dark:bg-amber-900/40",
            percentRemaining < 40 && "bg-red-100 dark:bg-red-900/40",
            getTextColor(percentRemaining)
          ] : "opacity-0"
        )}>
          {percentRemaining !== null ? `${percentRemaining}%` : '—'}
        </span>
      </div>
      
      {/* Progress bar - FIXED HEIGHT & MARGINS for consistent positioning */}
      <div className="h-1.5 bg-background/60 rounded-full overflow-hidden shadow-inner mb-1.5">
        <div 
          className={cn(
            "h-full rounded-full transition-all duration-700 ease-out",
            getProgressColor(percentRemaining)
          )}
          style={{ width: hasLifeData ? `${Math.max(percentRemaining || 0, 5)}%` : '0%' }}
        />
      </div>

      {/* Numbers row - FIXED HEIGHT with consistent baseline alignment */}
      <div className="flex items-center justify-center gap-2 h-5">
        <div className="flex items-center gap-0.5">
          <Clock className="w-2.5 h-2.5 text-primary/70 flex-shrink-0" />
          <span className={cn(
            "font-bold text-[11px] tabular-nums leading-none",
            !hasData && "text-muted-foreground"
          )}>
            {hours != null ? hours.toLocaleString() : '—'}
          </span>
          <span className="text-[8px] text-muted-foreground font-medium leading-none">hrs</span>
        </div>
        <div className="w-px h-3 bg-border/50 flex-shrink-0" />
        <div className="flex items-center gap-0.5">
          <SecondaryIcon className="w-2.5 h-2.5 text-primary/70 flex-shrink-0" />
          <span className={cn(
            "font-bold text-[11px] tabular-nums leading-none",
            secondaryValue == null && "text-muted-foreground"
          )}>
            {secondaryValue != null ? secondaryValue.toLocaleString() : '—'}
          </span>
          <span className="text-[8px] text-muted-foreground font-medium leading-none">{secondaryLabel}</span>
        </div>
      </div>
    </div>
  );
};

export default LifeIndicator;
